import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'react-feather';
import StyledTooltip from '../tooltip';
import type { FilterDropdownProps } from './filter-dropdown.props.ts';

const DEFAULT_TRIGGER_CLASS =
  'w-full px-3 text-sm border border-gray-200 rounded-lg bg-gray-50 text-[#6A6A6A] focus:outline-none focus:ring-2 focus:ring-[#0A8BC7] text-left flex items-center justify-between';

function FilterDropdown(props: FilterDropdownProps) {
  const {
    options,
    placeholder,
    className = DEFAULT_TRIGGER_CLASS,
    multiple,
    value,
    onChange,
    clearValue,
    showClear,
    selectAllLabel,
    searchable,
    searchPlaceholder = 'Search...',
    disabled = false,
    id,
    verticalPadding = 2,
  } = props;

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const portalIdRef = useRef(
    `filter-dropdown-portal-${Math.random().toString(36).slice(2)}`,
  ).current;

  const isMultiple = multiple === true;

  const filteredOptions =
    searchable && searchQuery.trim()
      ? options.filter((opt) => opt.label.toLowerCase().includes(searchQuery.toLowerCase().trim()))
      : options;

  useEffect(() => {
    if (open && searchable) {
      setSearchQuery('');
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else if (!open) {
      setSearchQuery('');
    }
  }, [open, searchable]);

  const updateDropdownPosition = () => {
    if (!buttonRef.current || !dropdownRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const shouldOpenUp = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

    const calculatedTop = shouldOpenUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4;

    setDropdownStyle({
      position: 'fixed',
      top: Math.max(8, calculatedTop), // viewport safety padding
      left: rect.left,
      width: rect.width,
      zIndex: 99999,
    });
  };

  useLayoutEffect(() => {
    if (open) {
      updateDropdownPosition();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return () => {};
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inButton = buttonRef.current?.contains(target);
      const inPortal = document.getElementById(portalIdRef)?.contains(target);

      if (!inButton && !inPortal) setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, portalIdRef]);

  useEffect(() => {
    if (!open) {
      return () => {};
    }

    const handleUpdate = () => {
      updateDropdownPosition();
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [open]);

  const getLabel = () => {
    if (isMultiple) {
      const arr = Array.isArray(value) ? value : [];
      return arr.length > 0 ? `${arr.length} selected` : placeholder;
    }
    return options.find((o) => o.value === value)?.label ?? placeholder;
  };
  const label = getLabel();

  const effectiveClearValue =
    !isMultiple && clearValue !== undefined ? (clearValue ?? options[0]?.value ?? 'all') : 'all';
  const hasActiveFilter = !isMultiple && showClear !== false && value !== effectiveClearValue;

  const handleOptionClick = (optValue: string) => {
    if (isMultiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optValue)
        ? currentValues.filter((v) => v !== optValue)
        : [...currentValues, optValue];
      (onChange as (v: string[]) => void)(newValues);
    } else {
      (onChange as (v: string) => void)(optValue);
      setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (!isMultiple) return;
    const allValues = options.map((o) => o.value);
    const currentValues = Array.isArray(value) ? value : [];
    const allSelected = allValues.length === currentValues.length;
    (onChange as (v: string[]) => void)(allSelected ? [] : allValues);
  };

  const dropdownList = (
    <div
      ref={dropdownRef}
      className="rounded-lg border border-gray-200 bg-white shadow-lg"
      style={dropdownStyle}
    >
      {searchable && (
        <div className="p-2 border-b border-gray-100">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A8BC7]"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <ul
        className="max-h-56 overflow-auto py-1 scrollbar-hide"
        role="listbox"
        aria-multiselectable={isMultiple}
      >
        {isMultiple && (
          <li
            role="option"
            aria-selected={Array.isArray(value) && value.length === options.length}
            tabIndex={0}
            className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
            onClick={handleSelectAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelectAll();
              }
            }}
          >
            <span className="text-sm text-[#393939]">{selectAllLabel ?? 'Select all'}</span>
          </li>
        )}

        {filteredOptions.map((opt) => {
          const isSelected = isMultiple
            ? Array.isArray(value) && value.includes(opt.value)
            : value === opt.value;
          return (
            <li
              key={opt.value}
              role="option"
              aria-selected={isSelected}
              tabIndex={0}
              className={`cursor-pointer px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                isSelected ? 'bg-[#0A8BC7]/10 text-[#0A8BC7] font-medium' : ''
              }`}
              onClick={() => handleOptionClick(opt.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOptionClick(opt.value);
                }
              }}
            >
              <StyledTooltip title={opt.label} placement="top">
                <span className="truncate flex-1">{opt.label}</span>
              </StyledTooltip>
              {isSelected && <Check className="h-4 w-4 text-[#0A8BC7]" />}
            </li>
          );
        })}
        {!isMultiple && hasActiveFilter && (
          <li
            role="option"
            aria-selected={value === effectiveClearValue}
            tabIndex={0}
            className="cursor-pointer px-3 py-2 text-sm text-[#EF4444] hover:bg-red-50 border-t border-gray-100 mt-1 pt-2"
            onClick={() => {
              (onChange as (v: string) => void)(effectiveClearValue);
              setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                (onChange as (v: string) => void)(effectiveClearValue);
                setOpen(false);
              }
            }}
          >
            <StyledTooltip title="Clear Filters" placement="top">
              <span>Clear Filters</span>
            </StyledTooltip>
          </li>
        )}
      </ul>
    </div>
  );

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`${className} py-${verticalPadding} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-[#6A6A6A]">{label}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open &&
        createPortal(
          <div id={portalIdRef} data-filter-dropdown-portal>
            {dropdownList}
          </div>,
          document.body,
        )}
    </div>
  );
}

FilterDropdown.defaultProps = {
  className: DEFAULT_TRIGGER_CLASS,
};

export default FilterDropdown;
